import { useMemo, useState } from 'react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PackageForm } from '@/components/packages/PackageForm'
import {
  useCollection,
  useAddDocument,
  useUpdateDocument,
  useDeleteDocument,
} from '@/hooks/useFirestore'
import { useTranslation } from 'react-i18next'
import { useLocaleFormat } from '@/hooks/useLocaleFormat'
import { enumLabel } from '@/lib/enumLabel'
import type { SubscriptionPackage } from '@/types'
import { Plus, Pencil, Trash2 } from 'lucide-react'

type PackageFormData = Omit<SubscriptionPackage, 'id' | 'created_at' | 'updated_at'>

export function PackagesPage() {
  const { t } = useTranslation()
  const { formatCurrency } = useLocaleFormat()
  const [editingPackage, setEditingPackage] = useState<SubscriptionPackage | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<SubscriptionPackage | null>(null)

  // Sorted here rather than with orderBy('display_order'): Firestore drops documents
  // that lack the ordered field, which hid packages saved without a display order.
  const { data: rawPackages, isLoading } = useCollection<SubscriptionPackage>('subscription_packages')
  const packages = useMemo(
    () =>
      [...(rawPackages ?? [])].sort(
        (a, b) =>
          (Number.isFinite(a.display_order) ? a.display_order : Number.MAX_SAFE_INTEGER) -
            (Number.isFinite(b.display_order) ? b.display_order : Number.MAX_SAFE_INTEGER) ||
          (a.name ?? '').localeCompare(b.name ?? '')
      ),
    [rawPackages]
  )

  const addPackage = useAddDocument('subscription_packages')
  const updatePackage = useUpdateDocument('subscription_packages')
  const deletePackage = useDeleteDocument('subscription_packages')

  const handleCreate = async (data: PackageFormData) => {
    await addPackage.mutateAsync(data)
    setShowCreateModal(false)
  }

  const handleUpdate = async (data: PackageFormData) => {
    if (!editingPackage) return
    await updatePackage.mutateAsync({ id: editingPackage.id, data })
    setEditingPackage(null)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    await deletePackage.mutateAsync(deleteTarget.id)
    setDeleteTarget(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          {t('packages.count', { count: packages.length })}
        </p>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4" />
          {t('packages.add')}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {packages.map((pkg) => (
          <Card key={pkg.id}>
            <CardContent className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{pkg.name}</h3>
                  <Badge variant={pkg.is_active ? 'success' : 'default'}>
                    {pkg.is_active ? t('common.active') : t('common.inactive')}
                  </Badge>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setEditingPackage(pkg)}
                    aria-label={t('common.edit')}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(pkg)}
                    aria-label={t('common.delete')}
                    className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <p className="text-xs text-gray-500">{pkg.description}</p>

              <div>
                <p className="text-lg font-bold text-gray-900">
                  {Number.isFinite(pkg.monthly_price_eur) ? formatCurrency(pkg.monthly_price_eur) : '–'}
                  <span className="text-sm font-normal text-gray-500">{t('packages.perMonth')}</span>
                </p>
                <p className="text-xs text-gray-500">
                  {t('packages.perYear', {
                    price: Number.isFinite(pkg.yearly_price_eur) ? formatCurrency(pkg.yearly_price_eur) : '–',
                  })}
                </p>
              </div>

              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-medium text-gray-700 mb-2">{t('packages.features')}</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(pkg.features || {})
                    .filter(([, v]) => v.enabled)
                    .map(([key]) => (
                      <span
                        key={key}
                        className="rounded bg-primary-50 px-1.5 py-0.5 text-[10px] text-primary-700"
                      >
                        {enumLabel(t, 'featureNames', key)}
                      </span>
                    ))}
                </div>
              </div>

              <div className="text-xs text-gray-500">
                {t('packages.maxCaregivers', { value: pkg.max_caregivers ?? t('common.unlimited') })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={t('packages.createTitle')}
        className="max-w-2xl"
      >
        <PackageForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          loading={addPackage.isPending}
        />
      </Modal>

      <Modal
        open={!!editingPackage}
        onClose={() => setEditingPackage(null)}
        title={t('packages.editTitle')}
        className="max-w-2xl"
      >
        {editingPackage && (
          <PackageForm
            initialData={editingPackage}
            onSubmit={handleUpdate}
            onCancel={() => setEditingPackage(null)}
            loading={updatePackage.isPending}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('packages.deleteTitle')}
        message={t('packages.deleteMessage', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('common.delete')}
        loading={deletePackage.isPending}
      />
    </div>
  )
}
